import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { MenuTrigger, Popover, type Selection, Text, useFilter } from 'react-aria-components';
import cx from 'clsx';
import { useIntl } from 'react-intl';
import { IconCrossOutlined } from '@repo/monochrome-icons';
import { Description } from '#components/form-elements/description/description';
import { FieldLabel } from '#components/form-elements/label/label';
import { Trigger } from '#components/form-elements/trigger/trigger';
import { Button } from '#components/button/button';
import { SearchFieldWithDebounce } from '#components/search-field-with-debounce/search-field-with-debounce';
import { MultiSelectTrigger } from './multi-select-trigger/multi-select-trigger';
import type { MultiSelectOptionType } from './multi-select-option/multi-select-option';
import { MultiSelectFooter } from './multi-select-footer/multi-select-footer';
import { MultiSelectFilter } from './multi-select-filter/multi-select-filter';
import { MultiSelectList } from './multi-select-list/multi-select-list';
import type { MultiSelectProps } from './types';
import styles from './multi-select.module.css';

export type { MultiSelectProps, MultiSelectOption } from './types';

export function MultiSelect<T extends MultiSelectOptionType>({
  label,
  className,
  description,
  errorMessage,
  placeholder,
  isOptional,
  isOptionalText,
  defaultOpen = false,
  onOpenChange,
  onClose,
  isDisabled,
  isReadOnly,
  isInvalid: isInvalidProp,
  fullWidth = true,
  selectedValueClassName,
  allowClear = false,
  clearButtonAriaLabel,
  secondaryText,
  selectedKeys = new Set(),
  onSelectionChange,
  customSelectedText,
  withSearch,
  withFilter,
  withFooter = false,
  items,
  renderOption,
  portalContainerId,
  optionClass,
}: MultiSelectProps<T>): ReactElement {
  const { formatMessage } = useIntl();
  const filter = useFilter({ sensitivity: 'base' });
  const [searchValue, setSearchValue] = useState('');
  const [internalFilterValue, setInternalFilterValue] = useState<string>(
    () => withFilter?.defaultValue ?? withFilter?.options[0]?.value ?? ''
  );
  const filterValue = withFilter?.filterValue ?? internalFilterValue;
  const [isOpen, setIsOpen] = useState(defaultOpen && !isReadOnly && !isDisabled);

  const handleOpenChange = useCallback(
    (open: boolean): void => {
      // Don't allow opening in readonly or disabled mode
      if ((isReadOnly || isDisabled) && open) {
        return;
      }
      setIsOpen(open);
      onOpenChange?.(open);
      if (!open) {
        onClose?.();
      }
    },
    [onOpenChange, isReadOnly, isDisabled, onClose]
  );

  const selectedText = useMemo((): string => {
    if (customSelectedText) return customSelectedText;

    if (selectedKeys.size === 0) return '';

    return items
      .filter(item => selectedKeys.has(item.key))
      .map(item => item.value)
      .join(', ');
  }, [selectedKeys, items, customSelectedText]);

  const handleClearAll = (): void => {
    onSelectionChange?.(new Set());
  };

  const showClearButton = allowClear && selectedKeys.size > 0 && !isDisabled && !isReadOnly;

  const sortedItems = useMemo(() => {
    const byFilter = withFilter
      ? items.filter(item => !filterValue || item[withFilter.filterKey] === filterValue)
      : items;
    const filtered = byFilter.filter(
      item => !searchValue || filter.contains(item.value, searchValue)
    );
    return filtered.sort((a, b) => {
      if (a.recommended && !b.recommended) return -1;
      if (!a.recommended && b.recommended) return 1;

      if (searchValue) {
        const aStartsWith = filter.startsWith(a.value, searchValue);
        const bStartsWith = filter.startsWith(b.value, searchValue);
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        const aWordStartsWith = a.value
          .split(/\s+/)
          .some(word => filter.startsWith(word, searchValue));
        const bWordStartsWith = b.value
          .split(/\s+/)
          .some(word => filter.startsWith(word, searchValue));
        if (aWordStartsWith && !bWordStartsWith) return -1;
        if (!aWordStartsWith && bWordStartsWith) return 1;
      }

      return 0;
    });
  }, [items, searchValue, filter, withFilter, filterValue]);

  const lastRecommendedIndex = useMemo(() => {
    return sortedItems.reduce((lastIndex, item, index) => {
      return item.recommended ? index : lastIndex;
    }, -1);
  }, [sortedItems]);

  const selectedVisibleCount = useMemo(() => {
    return sortedItems.filter(item => selectedKeys.has(item.key)).length;
  }, [sortedItems, selectedKeys]);

  const isIndeterminate = selectedVisibleCount > 0 && selectedVisibleCount < sortedItems.length;
  const isAllSelected = selectedVisibleCount === sortedItems.length && sortedItems.length > 0;

  const handleSelectAllToggle = useCallback((): void => {
    if (selectedKeys.size === 0) {
      const allVisibleKeys = new Set(
        sortedItems.filter(item => !item.disabled).map(item => item.key)
      );
      onSelectionChange?.(allVisibleKeys);
    } else {
      onSelectionChange?.(new Set());
    }
  }, [selectedKeys.size, sortedItems, onSelectionChange]);

  const handleListBoxSelectionChange = (keys: Selection): void => {
    if (keys === 'all') {
      const allKeys = new Set(sortedItems.map(item => item.key));
      onSelectionChange?.(allKeys);
    } else {
      onSelectionChange?.(keys);
    }
  };

  const handleCheckboxToggle = useCallback(
    (itemKey: string): void => {
      const newSelection = new Set(selectedKeys);
      if (newSelection.has(itemKey)) {
        newSelection.delete(itemKey);
      } else {
        newSelection.add(itemKey);
      }
      onSelectionChange?.(newSelection);
    },
    [selectedKeys, onSelectionChange]
  );

  // react-aria modal escapes the test container, we render it where it can find the modal (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER =
    ((portalContainerId ? document.getElementById(portalContainerId) : undefined) ||
      document.getElementById('ember-testing')) ??
    undefined;

  const isInvalid = Boolean(errorMessage) || isInvalidProp;

  const onSearch = useCallback(
    (query: string) => {
      if (typeof withSearch?.setSearchValue === 'function') {
        withSearch.setSearchValue(query);
      } else {
        setSearchValue(query);
      }
    },
    [withSearch, setSearchValue]
  );

  const onFilterChange = useCallback(
    (value: string): void => {
      if (value === filterValue) {
        return;
      }
      if (typeof withFilter?.setFilterValue === 'function') {
        withFilter.setFilterValue(value);
      } else {
        setInternalFilterValue(value);
      }
      onSearch('');
      if (withFilter?.clearSelectionOnFilterChange !== false) {
        onSelectionChange?.(new Set());
      }
    },
    [withFilter, onSearch, onSelectionChange, filterValue]
  );

  return (
    <div className={cx(styles.container, className)}>
      {Boolean(label) && (
        <FieldLabel>
          {label} {Boolean(isOptional) && isOptionalText}
        </FieldLabel>
      )}
      <div className={styles.triggerWithClear} data-invalid={isInvalid}>
        <MenuTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
          <Trigger
            className={styles.inputContainer}
            data-testid="multi-select-trigger"
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
          >
            <MultiSelectTrigger
              isOpen={isOpen}
              placeholder={placeholder}
              secondaryText={secondaryText}
              selectedCount={selectedKeys.size}
              selectedText={selectedText}
              selectedValueClassName={selectedValueClassName}
              withClear={showClearButton}
            />
          </Trigger>
          <Popover
            UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
            className={cx('body-standard', styles.popover, fullWidth && styles.fullWidth)}
            data-testid="multi-select-dropdown"
            offset={8}
            placement="bottom start"
          >
            <div className={styles.popoverContent}>
              {withFilter ? (
                <MultiSelectFilter
                  label={withFilter.label}
                  onChange={onFilterChange}
                  options={withFilter.options}
                  selectedValue={filterValue}
                />
              ) : null}
              {withSearch ? (
                <SearchFieldWithDebounce
                  data-testid="multi-select-search-input"
                  inputClassName={styles.search}
                  onChange={onSearch}
                  placeholder={withSearch.placeholder}
                  value={withSearch.searchValue ?? searchValue}
                />
              ) : null}

              <MultiSelectList
                aria-label={label ?? placeholder}
                emptyMessage={withSearch?.emptyMessage}
                isOpen={isOpen}
                items={sortedItems}
                lastRecommendedIndex={lastRecommendedIndex}
                onItemToggle={handleCheckboxToggle}
                onSelectionChange={handleListBoxSelectionChange}
                optionClass={optionClass}
                renderOption={renderOption}
                selectedKeys={selectedKeys}
              />

              {sortedItems.length > 0 && withFooter ? (
                <MultiSelectFooter
                  isAllSelected={isAllSelected}
                  isIndeterminate={isIndeterminate}
                  onToggleAll={handleSelectAllToggle}
                />
              ) : null}
            </div>
          </Popover>
        </MenuTrigger>
        {showClearButton ? (
          <Button
            aria-label={clearButtonAriaLabel ?? formatMessage({ id: 'datepicker-btn-clear' })}
            className={styles.clearButton}
            data-testid="multi-select-clear-button"
            iconOnly
            onPress={handleClearAll}
            size="small"
            variant="tertiary"
          >
            <IconCrossOutlined aria-hidden />
          </Button>
        ) : null}
      </div>
      {errorMessage ? (
        <Text className={cx(styles.error, 'caption')} role="alert" slot="errorMessage">
          {errorMessage}
        </Text>
      ) : null}
      {Boolean(description) && <Description>{description}</Description>}
    </div>
  );
}
